import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import { Tabs, message } from "antd";
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../CartContext';
import ProductService from './Productservice';
import '@fortawesome/fontawesome-free/css/all.min.css';

const Products = () => {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState({});
  
  const [filterOptions, setFilterOptions] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    priceRanges: [],
    brands: [],
    availability: []
  });
  const [sortBy, setSortBy] = useState('name');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 12,
    totalCount: 0,
    totalPages: 0
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await ProductService.getAllProducts();
        console.log('Raw data from API:', data);
        
        const mappedProducts = data.map(product => ProductService.mapProductToFrontend(product));
        console.log('Mapped products:', mappedProducts);
        
        setAllProducts(mappedProducts);
        setProducts(mappedProducts);
        
        const categoriesMap = {};
        mappedProducts.forEach(product => {
          if (product.category && product.subCategory) {
            if (!categoriesMap[product.category]) {
              categoriesMap[product.category] = new Set();
            }
            categoriesMap[product.category].add(product.subCategory);
          }
        });
        
        const categoriesObj = {};
        Object.keys(categoriesMap).sort().forEach(cat => {
          categoriesObj[cat] = Array.from(categoriesMap[cat]).sort();
        });
        
        console.log('Generated categories:', categoriesObj);
        setCategories(categoriesObj);
        
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Erreur lors du chargement des produits. Veuillez réessayer.');
        message.error('Impossible de charger les produits');
        setProducts([]);
        setCategories({});
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const generateClientSideFilterOptions = useCallback(() => {
    if (!selectedCategory || !selectedSubCategory) return null;

    const categoryProducts = allProducts.filter(p => 
      p.category === selectedCategory && 
      p.subCategory === selectedSubCategory
    );

    if (categoryProducts.length === 0) return null;

    const brandsMap = {};
    categoryProducts.forEach(p => {
      if (p.brand) {
        brandsMap[p.brand] = (brandsMap[p.brand] || 0) + 1;
      }
    });
    const brands = Object.entries(brandsMap).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);

    const prices = categoryProducts.map(p => p.price).filter(p => p > 0);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceStep = (maxPrice - minPrice) / 4;

    const priceRanges = [
      {
        label: `Moins de ${Math.round(minPrice + priceStep)} DT`,
        minPrice: 0,
        maxPrice: minPrice + priceStep,
        count: categoryProducts.filter(p => p.price <= minPrice + priceStep).length
      },
      {
        label: `${Math.round(minPrice + priceStep)} - ${Math.round(minPrice + priceStep * 2)} DT`,
        minPrice: minPrice + priceStep,
        maxPrice: minPrice + priceStep * 2,
        count: categoryProducts.filter(p => p.price > minPrice + priceStep && p.price <= minPrice + priceStep * 2).length
      },
      {
        label: `${Math.round(minPrice + priceStep * 2)} - ${Math.round(minPrice + priceStep * 3)} DT`,
        minPrice: minPrice + priceStep * 2,
        maxPrice: minPrice + priceStep * 3,
        count: categoryProducts.filter(p => p.price > minPrice + priceStep * 2 && p.price <= minPrice + priceStep * 3).length
      },
      {
        label: `Plus de ${Math.round(minPrice + priceStep * 3)} DT`,
        minPrice: minPrice + priceStep * 3,
        maxPrice: Infinity,
        count: categoryProducts.filter(p => p.price > minPrice + priceStep * 3).length
      }
    ].filter(range => range.count > 0);

    // Calculer la disponibilité
    const inStockCount = categoryProducts.filter(p => p.inStock === true).length;
    const onOrderCount = categoryProducts.filter(p => p.inStock === false).length;

    const availabilityOptions = [];
    if (inStockCount > 0) {
      availabilityOptions.push({
        label: 'En stock',
        value: 'in_stock',
        count: inStockCount
      });
    }
    if (onOrderCount > 0) {
      availabilityOptions.push({
        label: 'Sur commande',
        value: 'on_order',
        count: onOrderCount
      });
    }

    return {
      brands,
      priceRanges,
      availabilityOptions,
      priceStats: {
        minPrice,
        maxPrice,
        averagePrice: prices.reduce((a, b) => a + b, 0) / prices.length
      }
    };
  }, [allProducts, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    if (selectedCategory && selectedSubCategory) {
      const clientOptions = generateClientSideFilterOptions();
      setFilterOptions(clientOptions);

      const loadFilterOptions = async () => {
        try {
          console.log('Loading filter options from API for:', selectedCategory, selectedSubCategory);
          const options = await ProductService.getFilterOptions(
            selectedCategory, 
            selectedSubCategory
          );
          console.log('Filter options from API:', options);
          setFilterOptions(options);
        } catch (error) {
          console.warn('Could not load filter options from API, using client-side options:', error);
        }
      };

      loadFilterOptions();
    }
  }, [selectedCategory, selectedSubCategory, generateClientSideFilterOptions]);

  const applyClientSideFilters = useCallback(() => {
    console.log('=== APPLYING CLIENT-SIDE FILTERS ===');
    console.log('Selected category:', selectedCategory);
    console.log('Selected subcategory:', selectedSubCategory);
    console.log('Search term:', searchTerm);
    console.log('Active filters:', activeFilters);

    let filtered = allProducts.filter(p => 
      p.category === selectedCategory && 
      p.subCategory === selectedSubCategory
    );

    console.log('Products after category filter:', filtered.length);

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(search) ||
        (p.reference && p.reference.toLowerCase().includes(search)) ||
        (p.barcode && p.barcode.toLowerCase().includes(search)) ||
        (p.description && p.description.toLowerCase().includes(search))
      );
      console.log('Products after search filter:', filtered.length);
    }

    if (activeFilters.priceRanges.length > 0) {
      filtered = filtered.filter(p => {
        return activeFilters.priceRanges.some(range => 
          p.price >= range.minPrice && p.price <= range.maxPrice
        );
      });
      console.log('Products after price filter:', filtered.length);
    }

    if (activeFilters.brands.length > 0) {
      filtered = filtered.filter(p => 
        activeFilters.brands.includes(p.brand)
      );
      console.log('Products after brand filter:', filtered.length);
    }

    if (activeFilters.availability.length > 0) {
      filtered = filtered.filter(p => {
        if (activeFilters.availability.includes('in_stock') && 
            activeFilters.availability.includes('on_order')) {
          return true;
        }
        if (activeFilters.availability.includes('in_stock')) {
          return p.inStock === true;
        }
        if (activeFilters.availability.includes('on_order')) {
          return p.inStock === false;
        }
        return true;
      });
      console.log('Products after availability filter:', filtered.length);
    }

    const sortedFiltered = [...filtered];
    switch (sortBy) {
      case 'price_asc':
        sortedFiltered.sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        sortedFiltered.sort((a, b) => b.price - a.price);
        break;
      case 'name_desc':
        sortedFiltered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        sortedFiltered.sort((a, b) => b.id - a.id);
        break;
      default:
        sortedFiltered.sort((a, b) => a.name.localeCompare(b.name));
    }

    console.log('Products after sorting:', sortedFiltered.length);

    const totalCount = sortedFiltered.length;
    const totalPages = Math.ceil(totalCount / pagination.pageSize);
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const paginatedProducts = sortedFiltered.slice(startIndex, startIndex + pagination.pageSize);

    console.log('Paginated products:', paginatedProducts.length);
    console.log('Total pages:', totalPages);

    setProducts(paginatedProducts);
    setPagination(prev => ({
      ...prev,
      totalCount,
      totalPages
    }));
  }, [allProducts, selectedCategory, selectedSubCategory, searchTerm, activeFilters, sortBy, pagination.currentPage, pagination.pageSize]);

  const applyFilters = useCallback(async () => {
    if (!selectedSubCategory) {
      console.log('No subcategory selected, skipping filter application');
      return;
    }

    console.log('=== APPLYING FILTERS ===');
    
    setLoading(true);
    
    setTimeout(() => {
      applyClientSideFilters();
      setLoading(false);
    }, 100);

    try {
      const filterRequest = {
        Category: selectedCategory,
        SubCategory: selectedSubCategory,
        SearchTerm: searchTerm || null,
        PriceRange: activeFilters.priceRanges.length > 0 ? {
          MinPrice: Math.min(...activeFilters.priceRanges.map(r => r.minPrice)),
          MaxPrice: Math.max(...activeFilters.priceRanges.map(r => r.maxPrice))
        } : null,
        Brands: activeFilters.brands.length > 0 ? activeFilters.brands : null,
        Availability: activeFilters.availability.length > 0 ? activeFilters.availability : null,
        SortBy: sortBy,
        PageNumber: pagination.currentPage,
        PageSize: pagination.pageSize
      };

      console.log('Attempting API filter (background):', filterRequest);
      const response = await ProductService.filterProducts(filterRequest);
      
      if (response && response.products && response.products.length > 0) {
        console.log('API filter successful, updating with API results');
        const mappedProducts = response.products.map(product => 
          ProductService.mapProductToFrontend(product)
        );
        
        setProducts(mappedProducts);
        setPagination({
          currentPage: response.pageNumber || 1,
          pageSize: response.pageSize || 12,
          totalCount: response.totalCount || mappedProducts.length,
          totalPages: response.totalPages || 1
        });
      }
    } catch (error) {
      console.log('API filter failed (expected), using client-side results:', error.message);
    }
  }, [selectedCategory, selectedSubCategory, searchTerm, activeFilters, sortBy, pagination.currentPage, pagination.pageSize, applyClientSideFilters]);

  useEffect(() => {
  if (selectedSubCategory) {
    console.log('Subcategory changed, applying filters');
    applyFilters();
  }
}, [selectedSubCategory, applyFilters]);

useEffect(() => {
  if (selectedSubCategory) {
    console.log('Filters changed, reapplying');
    applyFilters();
  }
}, [activeFilters, sortBy, searchTerm, pagination.currentPage, selectedSubCategory, applyFilters]);
  const handlePriceFilterChange = (priceRange, checked) => {
    setActiveFilters(prev => ({
      ...prev,
      priceRanges: checked
        ? [...prev.priceRanges, priceRange]
        : prev.priceRanges.filter(r => r.label !== priceRange.label)
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleBrandFilterChange = (brand, checked) => {
    setActiveFilters(prev => ({
      ...prev,
      brands: checked
        ? [...prev.brands, brand]
        : prev.brands.filter(b => b !== brand)
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleAvailabilityFilterChange = (availability, checked) => {
    setActiveFilters(prev => ({
      ...prev,
      availability: checked
        ? [...prev.availability, availability]
        : prev.availability.filter(a => a !== availability)
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };
  
  const handleTabChange = () => {
    setSelectedSubCategory(null);
    setSelectedCategory(null);
    setSearchTerm('');
    setActiveFilters({ priceRanges: [], brands: [], availability: [] });
    setProducts(allProducts);
    setFilterOptions(null);
  };
  
  const handleCategoryChange = (activeKey) => {
    setSelectedCategory(activeKey);
    setSelectedSubCategory(null);
    setSearchTerm('');
    setActiveFilters({ priceRanges: [], brands: [], availability: [] });
    setFilterOptions(null);
  };

  const handleSubCategorySelect = (subCategory) => {
    console.log('Subcategory selected:', subCategory);
    setSelectedSubCategory(subCategory);
    setActiveFilters({ priceRanges: [], brands: [], availability: [] });
    setPagination({ currentPage: 1, pageSize: 12, totalCount: 0, totalPages: 0 });
    setSearchTerm('');
  };
  
  const handleGoBack = () => navigate(-1);

  const getFilteredSubCategories = useCallback((category) => {
    const subCategories = categories[category] || [];
    
    if (searchTerm === '') {
      return subCategories.filter(subCat =>
        allProducts.some(p => p.subCategory === subCat)
      );
    }
    
    return subCategories.filter(subCat =>
      allProducts.some(p => 
        p.subCategory === subCat &&
        (p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         (p.reference && p.reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
         (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase())))
      )
    );
  }, [categories, allProducts, searchTerm]);

  const renderProductCard = (product) => (
    <div key={product.id} className="card" onClick={() => navigate(`/product/${product.id}`)}>
      <div className="card-image-container">
        {(product.freeDelivery || product.subCategory === "Ordinateurs portables") && (
          <div className="badge-delivery">
            <i className="fas fa-truck"></i>
            <span>Livraison Gratuite</span>
          </div>
        )}
        <img
          src={product.adresse_Image_Product || 'https://via.placeholder.com/300x200?text=Produit'}
          alt={product.name}
          className="card-image"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/300x200?text=Image+non+disponible';
          }}
        />
      </div>
      <div className="card-content">
        {product.reference && (
          <p className="card-reference">Réf : {product.reference}</p>
        )}
        {product.brand && (
          <p className="card-brand">{product.brand}</p>
        )}
        <h3 className="card-title">{product.name}</h3>
        {product.description && (
          <p className="card-description">{product.description.substring(0, 100)}...</p>
        )}
        <div className="card-footer">
          <p className="card-price">{product.price.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DT</p>
          {product.inStock && (
            <p className="stock-status">
              <i className="fas fa-check-circle"></i> En stock
            </p>
          )}
        </div>
        <button className="btn-buy" onClick={(e) => {
          e.stopPropagation();
          navigate(`/product/${product.id}`);
        }}>
          <i className="fas fa-shopping-cart"></i>
          J'achète
        </button>
      </div>
    </div>
  );

  const renderSubCategoryCard = (subCategory) => {
    const defaultImages = {
      "Ordinateurs portables": "https://novacustom.com/storage/Clevo-NJ50MU.png",
      "Processeurs": "https://media.topachat.com/media/s400/689b496c228e0f45756b24bc.jpg",
      "Claviers": "https://www.graiet.tn/media/catalog/product/cache/fb4fbd9d41acbe50a1e96292a8b8604b/0/2/0208014_hp_clavier_sans_fil_hp_k2500_noir_--.jpg",
      "Casques": "https://www.samsungshop.tn/14893-large_default/jbl-live500bt.jpg",
      "Disques durs (HDD, SSD)": "https://tunewtec.com/wp-content/uploads/2023/06/disque-externe-toshiba-2to-HDTB520EK3AA.jpg",
      "Routeurs": "https://spacenet.tn/86016-large_default/routeur-sans-fil-d-link-wifi-300mbps-noir-dir-612ena.jpg",
      "Switches réseau": "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTxkNxMrQYmMbIPumNA9M4aRdzmHP7QZ1oX-w&s",
      "Points d'accès Wi-Fi": "https://www.netgear.com/zone2/cid/fit/1024x633/https/www.netgear.com/fr/media/WAX220-20230417-img4-hero_tcm169-145713.jpg",
    };
    /* eslint-disable no-unused-vars */
    const productCount = allProducts.filter(p => p.subCategory === subCategory).length;
    
    return (
      <div 
        key={subCategory} 
        className="subcategory-card"
        onClick={() => handleSubCategorySelect(subCategory)}
      >
        <div className="subcategory-image-container">
          <img
            src={defaultImages[subCategory] || 'https://via.placeholder.com/300x200?text=' + encodeURIComponent(subCategory)}
            alt={subCategory}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/300x200?text=' + encodeURIComponent(subCategory);
            }}
          />
         
        </div>
        <div className="subcategory-title">
          <h3>{subCategory}</h3>
          <i className="fas fa-arrow-right"></i>
        </div>
      </div>
    );
  };

  const renderFilterSidebar = () => {
    if (!filterOptions) {
      return (
        <div className="filter-sidebar">
          <h3 className="filter-title"><i className="fas fa-filter"></i> Filtrer par</h3>
          <div className="loading-filters">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Chargement des filtres...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="filter-sidebar">
        <h3 className="filter-title"><i className="fas fa-filter"></i> Filtrer par</h3>
        
        <div className="filter-group">
          <h4>Trier par</h4>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="name">Nom (A-Z)</option>
            <option value="name_desc">Nom (Z-A)</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
            <option value="newest">Plus récent</option>
          </select>
        </div>

        {filterOptions.priceRanges && filterOptions.priceRanges.length > 0 && (
          <div className="filter-group">
            <h4>Prix</h4>
            {filterOptions.priceRanges.map(range => (
              <label key={range.label} className="filter-checkbox">
                <input 
                  type="checkbox"
                  checked={activeFilters.priceRanges.some(r => r.label === range.label)}
                  onChange={(e) => handlePriceFilterChange(range, e.target.checked)}
                />
                <span>{range.label} ({range.count})</span>
              </label>
            ))}
          </div>
        )}

        {filterOptions.availabilityOptions && filterOptions.availabilityOptions.length > 0 && (
          <div className="filter-group">
            <h4>Disponibilité</h4>
            {filterOptions.availabilityOptions.map(option => (
              <label key={option.value} className="filter-checkbox">
                <input 
                  type="checkbox"
                  checked={activeFilters.availability.includes(option.value)}
                  onChange={(e) => handleAvailabilityFilterChange(option.value, e.target.checked)}
                />
                <span>{option.label} ({option.count})</span>
              </label>
            ))}
          </div>
        )}

        {filterOptions.brands && filterOptions.brands.length > 0 && (
          <div className="filter-group">
            <h4>Marque</h4>
            <div className="filter-scroll">
              {filterOptions.brands.map(brand => (
                <label key={brand.name} className="filter-checkbox">
                  <input 
                    type="checkbox"
                    checked={activeFilters.brands.includes(brand.name)}
                    onChange={(e) => handleBrandFilterChange(brand.name, e.target.checked)}
                  />
                  <span>{brand.name} ({brand.count})</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination">
        <button 
          onClick={() => setPagination(prev => ({ ...prev, currentPage: 1 }))}
          disabled={pagination.currentPage === 1}
          className="pagination-btn"
          title="Première page"
        >
          <i className="fas fa-angle-double-left"></i>
        </button>

        <button 
          onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
          disabled={pagination.currentPage === 1}
          className="pagination-btn"
        >
          <i className="fas fa-chevron-left"></i> Précédent
        </button>
        
        <div className="pagination-numbers">
          {startPage > 1 && (
            <>
              <button 
                className="pagination-number"
                onClick={() => setPagination(prev => ({ ...prev, currentPage: 1 }))}
              >
                1
              </button>
              {startPage > 2 && <span className="pagination-ellipsis">...</span>}
            </>
          )}

          {pages.map(page => (
            <button
              key={page}
              className={`pagination-number ${page === pagination.currentPage ? 'active' : ''}`}
              onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
            >
              {page}
            </button>
          ))}

          {endPage < pagination.totalPages && (
            <>
              {endPage < pagination.totalPages - 1 && <span className="pagination-ellipsis">...</span>}
              <button 
                className="pagination-number"
                onClick={() => setPagination(prev => ({ ...prev, currentPage: pagination.totalPages }))}
              >
                {pagination.totalPages}
              </button>
            </>
          )}
        </div>
        
        <button 
          onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
          disabled={pagination.currentPage === pagination.totalPages}
          className="pagination-btn"
        >
          Suivant <i className="fas fa-chevron-right"></i>
        </button>

        <button 
          onClick={() => setPagination(prev => ({ ...prev, currentPage: pagination.totalPages }))}
          disabled={pagination.currentPage === pagination.totalPages}
          className="pagination-btn"
          title="Dernière page"
        >
          <i className="fas fa-angle-double-right"></i>
        </button>
      </div>
    );
  };

  if (loading && products.length === 0 && !selectedSubCategory) {
    return (
      <div className="products-container">
        <section className="products-section loading-section">
          <div className="loading-message">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Chargement des produits...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="products-container">
        <section className="products-section error-section">
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            <h3>Erreur</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="btn-retry">
              <i className="fas fa-redo"></i> Réessayer
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (Object.keys(categories).length === 0 && !loading) {
    return (
      <div className="products-container">
        <section className="products-section">
          <div className="empty-state">
            <i className="fas fa-box-open"></i>
            <h3>Aucune catégorie disponible</h3>
            <p>Aucun produit avec catégorie n'a été trouvé dans la base de données</p>
            <button onClick={() => window.location.reload()} className="btn-retry">
              <i className="fas fa-redo"></i> Actualiser
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="products-container">
      <div className="products-topbar">
        <div className="topbar-cart" onClick={() => navigate('/cart')}>
          <i className="fas fa-shopping-cart"></i>
          {cartCount > 0 && (
            <span className="topbar-badge">{cartCount}</span>
          )}
        </div>
      </div>

      <section className="products-section">
        <div className="section-header">
          <div className="header-top">
            <button 
              className="back-arrow-btn" 
              onClick={handleGoBack} 
              title="Retour"
              style={{ color: '#0099ff' }}
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <h1 className="section-title">Nos Produits</h1>
              <p className="section-subtitle">Découvrez notre catalogue complet de solutions informatiques</p>
            </div>
          </div>
        </div>

        <Tabs 
          type="card" 
          className="main-tabs" 
          onChange={handleTabChange}
          items={[
            {
              key: '1',
              label: <span><i className="fas fa-cogs"></i> Systèmes d'Informations</span>,
              children: (
                <div className="empty-state">
                  <i className="fas fa-cogs"></i>
                  <h3>Cette section sera bientôt disponible</h3>
                  <p>Contactez-nous pour un devis personnalisé sur mesure</p>
                  <button className="btn-contact" onClick={() => navigate('/contact')}>
                    <i className="fas fa-envelope"></i> Nous contacter
                  </button>
                </div>
              ),
            },
            {
              key: '2',
              label: <span><i className="fas fa-laptop"></i> Matériels Informatiques et Accessoires</span>,
              children: (
                <Tabs 
                  tabPosition="left" 
                  className="category-tabs" 
                  onChange={handleCategoryChange}
                  items={Object.entries(categories).map(([category, subCategories]) => ({
                    key: category,
                    label: category,
                    children: (
                      <>
                        {!selectedSubCategory ? (
                          <div className="category-content">
                            <div className="search-container">
                              <i className="fas fa-search search-icon"></i>
                              <input
                                type="text"
                                placeholder={`Rechercher dans ${category}...`}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="search-input"
                              />
                            </div>

                            <div className="category-info">
                              <h2>{category}</h2>
                            </div>

                            <div className="subcategory-grid">
                              {getFilteredSubCategories(category).map(renderSubCategoryCard)}
                            </div>
                            
                            {getFilteredSubCategories(category).length === 0 && (
                              <div className="empty-state">
                                <i className="fas fa-search"></i>
                                <h3>Aucune sous-catégorie trouvée</h3>
                                <p>Essayez avec d'autres mots-clés</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="products-layout">
                            {renderFilterSidebar()}

                            <div className="products-main">
                              <div className="products-header">
                                <button 
                                  className="back-btn" 
                                  onClick={() => {
                                    setSelectedSubCategory(null);
                                    setSearchTerm('');
                                    setActiveFilters({ priceRanges: [], brands: [], availability: [] });
                                  }}
                                >
                                  <i className="fas fa-arrow-left" style={{ color: '#0099ff' }}></i>
                                </button>
                                
                                <div className="breadcrumb">
                                  <span>{category}</span>
                                  <i className="fas fa-chevron-right"></i>
                                  <span className="active">{selectedSubCategory}</span>
                                </div>
                              </div>

                              <div className="search-container">
                                <i className="fas fa-search search-icon"></i>
                                <input
                                  type="text"
                                  placeholder={`Rechercher dans ${selectedSubCategory}...`}
                                  value={searchTerm}
                                  onChange={handleSearchChange}
                                  className="search-input"
                                />
                              </div>

                              {loading ? (
                                <div className="loading-message">
                                  <i className="fas fa-spinner fa-spin"></i>
                                  <p>Chargement...</p>
                                </div>
                              ) : products.length > 0 ? (
                                <>
                                  <div className="product-grid">
                                    {products.map(renderProductCard)}
                                  </div>
                                  {renderPagination()}
                                </>
                              ) : (
                                <div className="empty-state">
                                  <i className="fas fa-box-open"></i>
                                  <h3>Aucun produit trouvé</h3>
                                  <p>Essayez de modifier vos critères de recherche ou de filtrage</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ),
                  }))}
                />
              ),
            },
          ]}
        />
      </section>
    </div>
  );
};

export default Products;